import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

/**
 * Annuaire du personnel — page "Personnel" du menu, même patron que customerSlice.js
 * (Clients) mais sur `/dashboard/staff/*`, réservé au SUPER_ADMIN.
 *
 * Le blocage/déblocage réutilise les routes génériques `/user/block/:id` et
 * `/user/unblock/:id` déjà utilisées pour les clients — elles opèrent sur
 * n'importe quel type de compte. La suppression, elle, a sa propre route
 * (`/dashboard/staff/:id`) qui refuse un staff encore affilié à un établissement.
 */

const initialState = {
  staff: [],
  loading: false,
  isSuccess: false,
  errors: []
};

export const fetchStaffDirectory = createAsyncThunk(
  'staffDirectory/fetchStaffDirectory',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/dashboard/staff/directory');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockStaff = createAsyncThunk(
  'staffDirectory/blockStaff',
  async ({ id, reason }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/block/${id}`, { reason });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const unblockStaff = createAsyncThunk(
  'staffDirectory/unblockStaff',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/unblock/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteStaff = createAsyncThunk(
  'staffDirectory/deleteStaff',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/dashboard/staff/${id}`);
      return id;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const staffDirectorySlice = createSlice({
  name: 'staffDirectory',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffDirectory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStaffDirectory.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.staff = Array.isArray(payload) ? payload : [];
        state.loading = false;
      })
      .addCase(fetchStaffDirectory.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message || [];
        state.loading = false;
      })
      .addCase(blockStaff.fulfilled, (state, { payload }) => {
        state.staff = state.staff.map((s) => (s._id === payload._id ? { ...s, locked: true } : s));
      })
      .addCase(unblockStaff.fulfilled, (state, { payload }) => {
        state.staff = state.staff.map((s) => (s._id === payload._id ? { ...s, locked: false } : s));
      })
      .addCase(deleteStaff.fulfilled, (state, { payload }) => {
        state.staff = state.staff.filter((s) => s._id !== payload);
      });
  }
});

export default staffDirectorySlice.reducer;
